import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, setIn, getIn } from 'formik';
import lodashTimes from 'lodash/times';
import lodashCompact from 'lodash/fp/compact';
import lodashUniq from 'lodash/fp/uniq';
import lodashGroupBy from 'lodash/fp/groupBy';
import moment from 'moment';

import SrApplicationAssemblyLineUpdateDocumentsTypeModal from 'client/components/SrApplicationAssemblyLine/SrApplicationAssemblyLineUpdateDocumentsTypeModal/SrApplicationAssemblyLineUpdateDocumentsTypeModal';

import typeCheck from 'common/util/typeCheck';
import emptyFunction from 'common/util/emptyFunction';
import capitalize from 'common/util/capitalize';
import makeFullName from 'common/util/makeFullName';

import { INCOME_VERIFICATION_STATUS_COMPLETED } from 'common/incomeVerificationConstants';

import {
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_TO_COLOR_MAPPING,
  SR_APPLICATION_ASSEMBLY_LINE_INCOME_MAX_DAYS_DIFF,
  getNextStatusIndex,
} from 'common/applicationAssemblyLineConstants';

import {
  PROOF_OF_INCOME_PAYTSTUBS,
  PROOF_OF_INCOME_OFFER_LETTER,
  PROOF_OF_INCOME_BANK_STATEMENTS,
  PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS,
  PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS,
  PROOF_OF_INCOME_TO_HOUSEHOLD_APPPLICATION_FIELD_MAPPING,
  PROOF_OF_INCOME_TYPE_TO_LABEL_MAPPING,
  PAYROLL_TYPE_MONTHLY,
  PAYROLL_TYPE_YEARLY,
  OFFER_LETTER_COMPENSATION_TYPE_HOURLY,
  OFFER_LETTER_COMPENSATION_TYPE_SALARY,
  OFFER_LETTER_COMPENSATION_TYPE_OPTION_TYPES,
  calculatePaystubsGrossMonthlyIncomeRaw,
  calculateAssemblyLineGrossMonthlyIncome,
  getInitialAssemblyLineVOIEFormState,
  getAssemblyLineVoieStatuses,
} from 'common/householdApplication/incomeHelpers';

import { formatPrice } from 'common/formatters';

import log from 'client/lib/log';

import {
  showFieldIf,
  hideFieldIf,
  createInitialValues,
  createFormValidationFn,
} from 'client/helpers/formikHelpers';

import SrFormikField, {
  SR_FIELD_THEME_ADOBE_ASSEMBLY_LINE,
  SR_FIELD_THEME_ADOBE_ASSEMBLY_LINE_ORANGE,
} from 'client/components/SrFormikField';

import SrApplicationAssemblyLineContainer from 'client/components/SrApplicationAssemblyLine/SrApplicationAssemblyLineContainer';
import SrIncomeVerificationCardAssemblyLine from 'client/components/SrApplicationAssemblyLine/SrIncomeVerificationCardAssemblyLine';
import {
  SR_COLLAPSIBLE_CONTAINER_BORDER_TYPE_BOTTOM_GRAY,
  SR_COLLAPSIBLE_CONTAINER_BORDER_TYPE_ROUNDED,
  SR_COLLAPSIBLE_CONTAINER_EDIT_BUTTON_THEME_BLUE,
} from 'client/components/SrCollapsibleContainer/SrCollapsibleContainer';
import SrModalSplitView from 'client/components/SrModalSplitView/SrModalSplitView';
import SrS3FileDisplay from 'client/components/SrS3FileDisplay';
import SrInfoTile, { SR_INFO_TILE_THEME_ADMIN_GREY } from 'client/components/SrInfoTile/SrInfoTile';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrButton, { SR_BUTTON_THEME_ASSEMBLY_LINE_BLUE } from 'client/components/SrButton/SrButton';
import SrErrorMessage, {
  SR_ERROR_MESSAGE_THEME_ORANGE,
} from 'client/components/SrErrorMessage/SrErrorMessage';
import SrIncomeVerificationTotalCardAssemblyLine from 'client/components/SrApplicationAssemblyLine/SrIncomeVerificationTotalCardAssemblyLine';
import SrText from 'client/components/SrText';

const debug = log.debug('sunroom:SrApplicationAssemblyLine');

const getFormFieldsByProofOfIncomeType = ({ values, householdApplication }) => {
  const applicantName = makeFullName(householdApplication.firstName, householdApplication.lastName);

  const offerLetterFields = [
    {
      id: 'offerLetterCompanyLetterHeadExistsOk',
      title: 'Is there a company letter head on the offer letter?',
      type: 'yes_no',
    },
    {
      id: 'offerLetterCompanyLetterHeadNameOk',
      title: `Was the offer letter issued to ${applicantName}?`,
      type: 'yes_no',
    },
    {
      id: 'offerLetterEmployersName',
      subtitle: "Employer's name",
      type: 'short_text',
    },
    {
      id: 'offerLetterCompensationType',
      subtitle: 'Type of compensation',
      type: 'select',
      properties: {
        choices: OFFER_LETTER_COMPENSATION_TYPE_OPTION_TYPES,
      },
    },
    showFieldIf(values.offerLetterCompensationType === OFFER_LETTER_COMPENSATION_TYPE_SALARY, {
      id: 'offerLetterSalaryAmount',
      subtitle: 'Salary Amount',
      type: 'currency',
    }),
    showFieldIf(values.offerLetterCompensationType === OFFER_LETTER_COMPENSATION_TYPE_HOURLY, {
      id: 'offerLetterSpecifiesHoursPerWeek',
      title: 'Does the offer letter say how many hours they’ll work per week?',
      type: 'yes_no',
    }),
    showFieldIf(
      values.offerLetterCompensationType === OFFER_LETTER_COMPENSATION_TYPE_HOURLY &&
        values.offerLetterSpecifiesHoursPerWeek,
      {
        id: 'offerLetterHoursPerWeek',
        subtitle: 'How many hours will the Applicant work per week?',
        type: 'number',
      },
    ),
    showFieldIf(values.offerLetterCompensationType === OFFER_LETTER_COMPENSATION_TYPE_HOURLY, {
      id: 'offerLetterHourlyWage',
      subtitle: 'Hourly Wage',
      type: 'currency',
    }),
    {
      id: 'offerLetterIssuedDate',
      subtitle: 'Date of offer letter',
      type: 'date_picker',
      properties: {
        placeholder: 'xx/xx/xxxx',
      },
    },
    {
      id: 'offerLetterStartDate',
      subtitle: 'Start date',
      type: 'date_picker',
      properties: {
        placeholder: 'Choose date...',
      },
    },
    {
      id: 'offerLetterVerified',
      title: 'Have you called the employer to verify that this offer letter is legit?',
      type: 'yes_no',
    },
    {
      id: 'offerLetterShouldCountIncomeSource',
      title: 'Should the income on this document contribute to their total income?',
      type: 'yes_no',
    },
    {
      id: 'offerLetterShouldIncludePartnerDocs',
      title: 'Do you want to show this document to the partner?',
      type: 'yes_no',
    },
    {
      id: 'offerLetterMissingInformationFieldIds',
      type: 'hidden',
    },
  ];

  const paystubFields = [
    ...lodashTimes((householdApplication.paystubPhotos || []).length, (index) => index).reduce(
      (memo, index) => [
        ...memo,
        ...[
          {
            id: `paystubs.${index}.fileUrl`,
            type: 'hidden',
          },
          {
            id: `paystubs.${index}.nameOnPaystubOk`,
            title: `Is the name on the Paystub ${applicantName}?`,
            type: 'yes_no',
          },
          {
            id: `paystubs.${index}.payPeriodStartDate`,
            subtitle: 'Pay Period Start Date',
            type: 'date_picker',
            properties: {
              placeholder: 'Choose date...',
            },
          },
          {
            id: `paystubs.${index}.payPeriodEndDate`,
            subtitle: 'Pay Period End Date',
            type: 'date_picker',
            properties: {
              placeholder: 'Choose date...',
            },
          },
          {
            id: `paystubs.${index}.grossAmount`,
            subtitle: 'Gross amount',
            type: 'currency',
          },
          {
            id: `paystubs.${index}.missingInformationFieldIds`,
            type: 'hidden',
          },
        ],
      ],
      [],
    ),
    {
      id: 'paystubsShouldCountIncomeSource',
      title: 'Should the income on these documents contribute to their total income?',
      type: 'yes_no',
    },
    {
      id: 'paystubsShouldIncludePartnerDocs',
      title: 'Do you want to show these documents to the partner?',
      type: 'yes_no',
    },
  ];

  const bankStatementFields = [
    ...lodashTimes(
      (householdApplication.bankStatementFileUrls || []).length,
      (index) => index,
    ).reduce(
      (memo, index) => [
        ...memo,
        ...[
          {
            id: `bankStatements.${index}.fileUrl`,
            type: 'hidden',
          },
          {
            id: `bankStatements.${index}.nameOnBankStatementOk`,
            title: `Is the name on the bank statement ${applicantName}?`,
            type: 'yes_no',
          },
          {
            id: `bankStatements.${index}.depositAmount`,
            subtitle: `Statement ${index + 1} Deposit Amount`,
            type: 'currency',
          },
          {
            id: `bankStatements.${index}.endingBalance`,
            subtitle: `Statement ${index + 1} Ending Balance`,
            type: 'currency',
          },
          {
            id: `bankStatements.${index}.startDate`,
            subtitle: `Statement ${index + 1} Start Date`,
            type: 'date_picker',
            properties: {
              placeholder: 'Choose date...',
            },
          },
          {
            id: `bankStatements.${index}.endDate`,
            subtitle: `Statement ${index + 1} End Date`,
            type: 'date_picker',
            properties: {
              placeholder: 'Choose date...',
            },
          },
          {
            id: `bankStatements.${index}.missingInformationFieldIds`,
            type: 'hidden',
          },
        ],
      ],
      [],
    ),
    {
      id: 'bankStatementsShouldCountIncomeSource',
      title: 'Should the income on these documents contribute to their total income?',
      type: 'yes_no',
    },
    {
      id: 'bankStatementsShouldIncludePartnerDocs',
      title: 'Do you want to show these documents to the partner?',
      type: 'yes_no',
    },
  ];

  const additionalProofOfIncomeFields = [
    ...lodashTimes(
      (householdApplication.additionalProofOfIncomePhotos || []).length,
      (index) => index,
    ).reduce(
      (memo, index) => [
        ...memo,
        ...[
          {
            id: `additionalProofOfIncomePhotos.${index}.fileUrl`,
            type: 'hidden',
          },
          {
            id: `additionalProofOfIncomePhotos.${index}.nameOnDocumentOk`,
            title: `Is the name on the document ${applicantName}?`,
            type: 'yes_no',
          },
          {
            id: `additionalProofOfIncomePhotos.${index}.grossAmount`,
            subtitle: `Document ${index + 1} Amount`,
            type: 'currency',
          },
          {
            id: `additionalProofOfIncomePhotos.${index}.payrollType`,
            subtitle: 'Is the amount yearly or monthly income?',
            type: 'select',
            properties: {
              choices: [
                {
                  label: 'Monthly',
                  value: PAYROLL_TYPE_MONTHLY,
                },
                {
                  label: 'Yearly',
                  value: PAYROLL_TYPE_YEARLY,
                },
              ],
            },
          },
          {
            id: `additionalProofOfIncomePhotos.${index}.shouldCountIncomeSource`,
            title: 'Should the income on these documents contribute to their total income?',
            type: 'yes_no',
          },
          {
            id: `additionalProofOfIncomePhotos.${index}.shouldIncludePartnerDocs`,
            title: 'Do you want to show these documents to the partner?',
            type: 'yes_no',
          },
          {
            id: `additionalProofOfIncomePhotos.${index}.missingInformationFieldIds`,
            type: 'hidden',
          },
        ],
      ],
      [],
    ),
  ];

  return {
    [PROOF_OF_INCOME_OFFER_LETTER]: offerLetterFields,
    [PROOF_OF_INCOME_PAYTSTUBS]: paystubFields,
    [PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS]: paystubFields,
    [PROOF_OF_INCOME_BANK_STATEMENTS]: bankStatementFields,
    [PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS]: additionalProofOfIncomeFields,
  };
};

export const getFormFields = ({ values, householdApplication, onChange, proofOfIncomeType }) => {
  typeCheck('proofOfIncomeType::Maybe String', proofOfIncomeType);
  typeCheck('householdApplication::NonEmptyObject', householdApplication);

  const formFieldsByProofOfIncomeType = getFormFieldsByProofOfIncomeType({
    values,
    householdApplication,
  });

  // Hides the form field groups based on the proof of income type.
  // Injects the `onChange` handler on every form field.
  const formFields = Object.keys(formFieldsByProofOfIncomeType)
    .reduce((memo, formFieldProofOfIncomeType) => {
      const existingFieldIdMap = memo.reduce((memo, { id }) => ({ ...memo, [id]: true }), {});

      const formFields = formFieldsByProofOfIncomeType[formFieldProofOfIncomeType].filter(
        ({ id }) => !existingFieldIdMap[id],
      );

      if (!proofOfIncomeType || proofOfIncomeType === formFieldProofOfIncomeType) {
        memo.push(...formFields);
      } else if (
        !(
          // Prevents duplicate "hidden" form fields on this edge scenario,
          // the PINWHEEL_PAYSTUBS and PAYSTUBS income types use basically the same fields.
          (
            (formFieldProofOfIncomeType === PROOF_OF_INCOME_PAYTSTUBS &&
              proofOfIncomeType === PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS) ||
            (formFieldProofOfIncomeType === PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS &&
              proofOfIncomeType === PROOF_OF_INCOME_PAYTSTUBS)
          )
        )
      ) {
        memo.push(...formFields.map((formField) => hideFieldIf(true, formField)));
      }
      return memo;
    }, [])
    .map((formField) => ({
      ...formField,
      properties: {
        ...(formField.properties || {}),
        skipOnChangeReRender: true,
        onChange: (...args) => {
          if ((formField.properties || {}).onChange) {
            (formField.properties || {}).onChange(...args);
          }
          if (onChange) {
            let value = args[0];
            if (typeof value === 'object' && value.target) {
              value = value.target.value;
            }
            onChange(formField.id, value);
          }
        },
      },
    }));

  return formFields;
};

const makeMissingInformationErrors = ({
  proofOfIncomeType,
  formFields,
  fieldGroup,
  title,
  missingInformationFieldIds,
}) => {
  const missingInformationErrors = missingInformationFieldIds
    .map((fieldId) => formFields.find((formField) => formField.id === fieldId))
    .map((formField) => ({
      fieldId: formField.id,
      fieldGroup,
      title,
      proofOfIncomeType,
      message: `Missing information for ${formField.title || formField.subtitle}.`,
    }));

  return missingInformationErrors;
};

export const getStepErrors = ({ formFields, values, assemblyLine, householdApplication }) => {
  const stepErrors = [];

  if (householdApplication.offerLetterFileUrl) {
    stepErrors.push(
      ...lodashCompact([
        typeof values.offerLetterCompanyLetterHeadExistsOk === 'boolean' &&
          !values.offerLetterCompanyLetterHeadExistsOk && {
            proofOfIncomeType: PROOF_OF_INCOME_OFFER_LETTER,
            fieldId: 'offerLetterCompanyLetterHeadExistsOk',
            fieldGroup: 'offerLetterFileUrl',
            title: 'Offer Letter',
            message:
              'Please ask the applicant to provide an offer letter with a company letter head.',
          },
        typeof values.offerLetterCompanyLetterHeadNameOk === 'boolean' &&
          !values.offerLetterCompanyLetterHeadNameOk && {
            proofOfIncomeType: PROOF_OF_INCOME_OFFER_LETTER,
            fieldId: 'offerLetterCompanyLetterHeadNameOk',
            fieldGroup: 'offerLetterFileUrl',
            title: 'Offer Letter',
            message: 'Please ask the applicant to provide an offer letter issued to the applicant.',
          },
        typeof values.offerLetterSpecifiesHoursPerWeek === 'boolean' &&
          !values.offerLetterSpecifiesHoursPerWeek && {
            proofOfIncomeType: PROOF_OF_INCOME_OFFER_LETTER,
            fieldId: 'offerLetterSpecifiesHoursPerWeek',
            fieldGroup: 'offerLetterFileUrl',
            title: 'Offer Letter',
            message:
              'Please ask the applicant to provide a document stating how ' +
              'many hours per week they are scheduled.',
          },
        values.offerLetterStartDate &&
          assemblyLine.processingTime.startDate &&
          moment(assemblyLine.processingTime.startDate).diff(values.offerLetterStartDate, 'days') >
            SR_APPLICATION_ASSEMBLY_LINE_INCOME_MAX_DAYS_DIFF && {
            proofOfIncomeType: PROOF_OF_INCOME_OFFER_LETTER,
            fieldId: 'offerLetterStartDate',
            fieldGroup: 'offerLetterFileUrl',
            title: 'Offer Letter',
            message:
              'Please ask the applicant to upload documents from within the past ' +
              `${SR_APPLICATION_ASSEMBLY_LINE_INCOME_MAX_DAYS_DIFF} days.`,
          },
        typeof values.offerLetterVerified === 'boolean' &&
          !values.offerLetterVerified && {
            proofOfIncomeType: PROOF_OF_INCOME_OFFER_LETTER,
            fieldId: 'offerLetterVerified',
            fieldGroup: 'offerLetterFileUrl',
            title: 'Offer Letter',
            message: 'Please call the company to verify the offer letter is legit.',
          },
        ...makeMissingInformationErrors({
          proofOfIncomeType: PROOF_OF_INCOME_OFFER_LETTER,
          formFields,
          fieldGroup: 'offerLetterFileUrl',
          title: 'Offer Letter',
          missingInformationFieldIds: values.offerLetterMissingInformationFieldIds,
        }),
      ]),
    );
  }

  stepErrors.push(
    ...(values.paystubs || []).reduce(
      (memo, paystub, index) =>
        lodashCompact([
          ...memo,
          typeof paystub.nameOnPaystubOk === 'boolean' &&
            !paystub.nameOnPaystubOk && {
              proofOfIncomeType: PROOF_OF_INCOME_PAYTSTUBS,
              fieldId: `paystubs.${index}.nameOnPaystubOk`,
              fieldGroup: `paystubPhotos[${index}]`,
              title: `Paystub ${index + 1}`,
              message: 'Missing name of renter that matches name on photo ID.',
            },
          paystub.payPeriodStartDate &&
            assemblyLine.processingTime.startDate &&
            moment(assemblyLine.processingTime.startDate).diff(paystub.payPeriodStartDate, 'days') >
              SR_APPLICATION_ASSEMBLY_LINE_INCOME_MAX_DAYS_DIFF && {
              proofOfIncomeType: PROOF_OF_INCOME_PAYTSTUBS,
              fieldId: `paystubs.${index}.payPeriodStartDate`,
              fieldGroup: `paystubPhotos[${index}]`,
              title: `Paystub ${index + 1}`,
              message:
                'Please ask the applicant to upload documents from within the past ' +
                `${SR_APPLICATION_ASSEMBLY_LINE_INCOME_MAX_DAYS_DIFF} days.`,
            },
          paystub.payPeriodStartDate &&
            paystub.payPeriodEndDate &&
            moment(paystub.payPeriodEndDate).isBefore(paystub.payPeriodStartDate) && {
              proofOfIncomeType: PROOF_OF_INCOME_PAYTSTUBS,
              fieldId: `paystubs.${index}.payPeriodEndDate`,
              fieldGroup: `paystubPhotos[${index}]`,
              title: `Paystub ${index + 1}`,
              message: "The bank statement's end date can not be earlier than the start date.",
            },
          ...makeMissingInformationErrors({
            formFields,
            proofOfIncomeType: PROOF_OF_INCOME_PAYTSTUBS,
            title: `Paystub ${index + 1}`,
            fieldGroup: `paystubPhotos[${index}]`,
            missingInformationFieldIds: paystub.missingInformationFieldIds.map(
              (fieldId) => `paystubs.${index}.${fieldId}`,
            ),
          }),
        ]),
      [],
    ),
  );

  stepErrors.push(
    ...(values.bankStatements || []).reduce(
      (memo, bankStatement, index) =>
        lodashCompact([
          ...memo,
          typeof bankStatement.nameOnBankStatementOk === 'boolean' &&
            !bankStatement.nameOnBankStatementOk && {
              proofOfIncomeType: PROOF_OF_INCOME_BANK_STATEMENTS,
              fieldId: `bankStatements.${index}.nameOnBankStatementOk`,
              fieldGroup: `bankStatementFileUrls[${index}]`,
              title: `Bank Statement ${index + 1}`,
              message: 'Missing name of renter that matches name on photo ID.',
            },
          bankStatement.startDate &&
            bankStatement.endDate &&
            moment(bankStatement.endDate).isBefore(bankStatement.startDate) && {
              proofOfIncomeType: PROOF_OF_INCOME_BANK_STATEMENTS,
              fieldId: `bankStatements.${index}.endDate`,
              fieldGroup: `bankStatementFileUrls[${index}]`,
              title: `Bank Statement ${index + 1}`,
              message: "The bank statement's end date can not be earlier than the start date.",
            },
          bankStatement.startDate &&
            assemblyLine.processingTime.startDate &&
            moment(assemblyLine.processingTime.startDate).diff(bankStatement.startDate, 'days') >
              SR_APPLICATION_ASSEMBLY_LINE_INCOME_MAX_DAYS_DIFF && {
              proofOfIncomeType: PROOF_OF_INCOME_BANK_STATEMENTS,
              fieldId: `bankStatements.${index}.startDate`,
              fieldGroup: `bankStatementFileUrls[${index}]`,
              title: `Bank Statement ${index + 1}`,
              message:
                'Please ask the applicant to upload documents from within the past ' +
                `${SR_APPLICATION_ASSEMBLY_LINE_INCOME_MAX_DAYS_DIFF} days.`,
            },
          ...makeMissingInformationErrors({
            formFields,
            proofOfIncomeType: PROOF_OF_INCOME_BANK_STATEMENTS,
            fieldGroup: `bankStatementFileUrls[${index}]`,
            title: `Bank Statement ${index + 1}`,
            missingInformationFieldIds: bankStatement.missingInformationFieldIds.map(
              (fieldId) => `bankStatements.${index}.${fieldId}`,
            ),
          }),
        ]),
      [],
    ),
  );

  for (let index = 0; index < (values.additionalProofOfIncomePhotos || []).length; index += 1) {
    const additionalProofOfIncome = values.additionalProofOfIncomePhotos[index];
    if (
      typeof additionalProofOfIncome.nameOnDocumentOk === 'boolean' &&
      !additionalProofOfIncome.nameOnDocumentOk
    ) {
      stepErrors.push({
        proofOfIncomeType: PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS,
        fieldId: `additionalProofOfIncome.${index}.nameOnDocumentOk`,
        fieldGroup: `additionalProofOfIncomePhotos[${index}]`,
        title: `Additional Proof Of Income ${index + 1}`,
        message: 'Missing name of renter that matches name on photo ID.',
      });
    }

    if (additionalProofOfIncome.missingInformationFieldIds.length) {
      stepErrors.push(
        ...makeMissingInformationErrors({
          formFields,
          proofOfIncomeType: PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS,
          fieldGroup: `additionalProofOfIncomePhotos[${index}]`,
          title: `Additional Proof Of Income ${index + 1}`,
          missingInformationFieldIds: additionalProofOfIncome.missingInformationFieldIds.map(
            (fieldId) => `additionalProofOfIncomePhotos.${index}.${fieldId}`,
          ),
        }),
      );
    }
  }

  debug('SrApplicationAssemblyLineStepIncomeVerification:stepErrors', stepErrors, values);

  return stepErrors;
};

const SrStepError = ({ stepError }) => (
  <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_ORANGE}>
    Information Required:
    <br />
    {stepError.message}
  </SrErrorMessage>
);

SrStepError.propTypes = {
  stepError: PropTypes.object.isRequired,
};

SrStepError.displayName = 'SrStepError';

const SrAssemblyLineFormFields = ({
  formFields,
  formikBag,
  stepErrors,
  missingInformationListFieldId,
  onMissingInformationClick,
  onContinueClick,
  payrollTypes,
}) => {
  debug('SrAssemblyLineFormFields', {
    formFields,
    formikBag,
    stepErrors,
    missingInformationListFieldId,
    onMissingInformationClick,
    onContinueClick,
    payrollTypes,
  });
  const stepErrorIds = stepErrors.map(({ fieldId }) => fieldId);
  const missingInformationFieldIds = missingInformationListFieldId
    ? getIn(formikBag.values, missingInformationListFieldId)
    : null;

  return (
    <>
      {formFields.reduce((memo, formField) => {
        const stepError = stepErrors.find(({ fieldId }) => formField.id === fieldId);

        const onFieldMissingInformationClick = () =>
          onMissingInformationClick
            ? onMissingInformationClick({
                missingInformationListFieldId,
                missingInformationFieldId: formField.id,
                formikBag,
              })
            : null;

        const isMissingInformationField = missingInformationFieldIds?.includes(
          formField.id.split('.').pop(),
        );

        const shouldShowErrorMessage =
          stepError && !stepError.message.startsWith('Missing information');

        const shouldShowMissingInformationCta =
          formField.type !== 'yes_no' && formField.type !== 'hidden' && onMissingInformationClick;

        return lodashCompact([
          ...memo,
          isMissingInformationField ? (
            <div style={{ marginBottom: '10px' }} onClick={onFieldMissingInformationClick}>
              <SrFormikField
                formField={{
                  id: `${formField.id}MissingInformation`,
                  subtitle: formField.subtitle,
                  type: 'short_text',
                  properties: {
                    disabled: true,
                    onClick: onFieldMissingInformationClick,
                    style: {
                      cursor: 'pointer',
                    },
                  },
                }}
                value="Missing Information"
                theme={SR_FIELD_THEME_ADOBE_ASSEMBLY_LINE_ORANGE}
              />
            </div>
          ) : (
            <SrFormikField
              key={formField.id}
              formField={formField}
              formikBag={formikBag}
              theme={
                stepErrorIds.includes(formField.id)
                  ? SR_FIELD_THEME_ADOBE_ASSEMBLY_LINE_ORANGE
                  : SR_FIELD_THEME_ADOBE_ASSEMBLY_LINE
              }
            />
          ),
          shouldShowMissingInformationCta && (
            <SrBox key={`${formField.id}MissingInformation`} textAlign="right">
              <SrText
                cursor="pointer"
                size="12"
                weight="500"
                onClick={onFieldMissingInformationClick}
              >
                Missing Information?
              </SrText>
            </SrBox>
          ),
          shouldShowErrorMessage && (
            <SrStepError key={`${stepError.fieldId}Error`} stepError={stepError} />
          ),
          formField.id.includes('payPeriodEndDate') && (
            <span className="title" style={{ fontWeight: '600', fontSize: '16px' }}>
              Paystub Frequency: {capitalize(payrollTypes?.payrollType?.frequency)}{' '}
              {payrollTypes?.payrollType?.days && `(${payrollTypes?.payrollType?.days} days)`}
            </span>
          ),
        ]);
      }, [])}
      {onContinueClick && (
        <SrButton theme={SR_BUTTON_THEME_ASSEMBLY_LINE_BLUE} hasBoxShadow onClick={onContinueClick}>
          Continue
        </SrButton>
      )}
    </>
  );
};

SrAssemblyLineFormFields.displayName = 'SrAssemblyLineFormFields';

SrAssemblyLineFormFields.propTypes = {
  formFields: PropTypes.array.isRequired,
  stepErrors: PropTypes.array.isRequired,
  formikBag: PropTypes.object.isRequired,
};

const SrApplicationAssemblyLineStepIncomeVerification = ({
  householdApplication,
  updateAssemblyLineApplication,
  status,
  assemblyLine,
}) => {
  const [statedMonthlyIncome, setStatedMonthlyIncome] = useState({
    householdApplicationId: householdApplication.id,
    monthlyIncome: householdApplication.monthlyIncome,
  });
  const [proofOfIncomeDocsModalOpen, setProofOfIncomeDocsModalOpen] = useState(null);
  const initialValues = getInitialAssemblyLineVOIEFormState(householdApplication);

  const [paystubExpandedState, setPaystubExpandedState] = useState(
    lodashTimes((householdApplication.paystubPhotos || []).length, (index) => index).reduce(
      (memo, index) => ({
        ...memo,
        [index]:
          index ===
          getNextStatusIndex(
            getAssemblyLineVoieStatuses({
              assemblyLineVoie: initialValues,
              processingTime: assemblyLine.processingTime,
              householdApplication,
            }).paystubs,
          ),
      }),
      {},
    ),
  );

  const [
    assemblyLineApplicationUpdateDocumentTypeModalProps,
    setAssemblyLineApplicationUpdateDocumentTypeModalProps,
  ] = useState(null);

  useEffect(() => {
    if (statedMonthlyIncome.householdApplicationId !== householdApplication.id) {
      setStatedMonthlyIncome({
        householdApplicationId: householdApplication.id,
        monthlyIncome: householdApplication.monthlyIncome,
      });
    }
  }, [householdApplication, statedMonthlyIncome]);

  const onClickPaystubRow = useCallback(
    (index) => {
      setPaystubExpandedState({
        ...paystubExpandedState,
        [index]: !paystubExpandedState[index],
      });
    },
    [paystubExpandedState, setPaystubExpandedState],
  );

  const [bankStatementExpandedState, setBankStatementExpandedState] = useState(
    lodashTimes((householdApplication.bankStatementFileUrls || []).length, (index) => index).reduce(
      (memo, index) => ({
        ...memo,
        [index]:
          index ===
          getNextStatusIndex(
            getAssemblyLineVoieStatuses({
              assemblyLineVoie: initialValues,
              processingTime: assemblyLine.processingTime,
              householdApplication,
            }).bankStatements,
          ),
      }),
      {},
    ),
  );

  const onClickBankStatementRow = useCallback(
    (index) => {
      setBankStatementExpandedState({
        ...bankStatementExpandedState,
        [index]: !bankStatementExpandedState[index],
      });
    },
    [bankStatementExpandedState, setBankStatementExpandedState],
  );

  const [additionalProofOfIncomeExpandedState, setAdditionalProofOfIncomeExpandedState] = useState(
    lodashTimes(
      (householdApplication.additionalProofOfIncomePhotos || []).length,
      (index) => index,
    ).reduce(
      (memo, index) => ({
        ...memo,
        [index]:
          index ===
          getNextStatusIndex(
            getAssemblyLineVoieStatuses({
              assemblyLineVoie: initialValues,
              processingTime: assemblyLine.processingTime,
              householdApplication,
            }).additionalProofOfIncomePhotos,
          ),
      }),
      {},
    ),
  );

  const onClickAdditionalProofOfIncomeRow = useCallback(
    (index) => {
      setAdditionalProofOfIncomeExpandedState({
        ...additionalProofOfIncomeExpandedState,
        [index]: !additionalProofOfIncomeExpandedState[index],
      });
    },
    [additionalProofOfIncomeExpandedState, setAdditionalProofOfIncomeExpandedState],
  );

  const onToggleViewDocuments = useCallback(
    (proofOfIncomeType) => {
      setProofOfIncomeDocsModalOpen(proofOfIncomeType);
      // TODO: MARK voieStartedDate, HANDLE voieCompletedDate
    },
    [setProofOfIncomeDocsModalOpen],
  );

  const onClickPaystubContinueButton = useCallback(
    (paystubIndex) => {
      setPaystubExpandedState(
        lodashTimes((householdApplication.paystubPhotos || []).length, (index) => index).reduce(
          (memo, index) => ({
            ...memo,
            [index]: index === paystubIndex + 1,
          }),
          {},
        ),
      );
    },
    [householdApplication.paystubPhotos],
  );

  const onClickBankStatementContinueButton = useCallback(
    (bankStatementIndex) => {
      setBankStatementExpandedState(
        lodashTimes(
          (householdApplication.bankStatementFileUrls || []).length,
          (index) => index,
        ).reduce(
          (memo, index) => ({
            ...memo,
            [index]: index === bankStatementIndex + 1,
          }),
          {},
        ),
      );
    },
    [householdApplication.bankStatementFileUrls],
  );

  const onClickAdditionalProofOfIncomeContinueButton = useCallback(
    (additionalProofOfIncomeIndex) => {
      setAdditionalProofOfIncomeExpandedState(
        lodashTimes(
          (householdApplication.additionalProofOfIncomePhotos || []).length,
          (index) => index,
        ).reduce(
          (memo, index) => ({
            ...memo,
            [index]: index === additionalProofOfIncomeIndex + 1,
          }),
          {},
        ),
      );
    },
    [householdApplication.additionalProofOfIncomePhotos, setAdditionalProofOfIncomeExpandedState],
  );

  const onUpdateAssemblyLineApplication = useCallback(
    ({ updates }) => {
      const assemblyLineApplicationId = householdApplication.assemblyLineApplication?.id;
      updateAssemblyLineApplication({
        assemblyLineApplicationId: assemblyLineApplicationId,
        updates,
      });
    },
    [updateAssemblyLineApplication, householdApplication.assemblyLineApplication],
  );

  const onClickCompleteButton = useCallback(
    ({ formikBag }) => {
      onUpdateAssemblyLineApplication({
        updates: formikBag.values,
      });
      setProofOfIncomeDocsModalOpen(false);
    },
    [onUpdateAssemblyLineApplication, setProofOfIncomeDocsModalOpen],
  );

  const onMissingInformationClick = useCallback(
    ({ missingInformationListFieldId, missingInformationFieldId, formikBag }) => {
      const missingInformationFieldIdBase = missingInformationFieldId.split('.').pop();
      const missingInformationList = getIn(formikBag.values, missingInformationListFieldId) || [];
      let newMissingInformationList;

      if (missingInformationList.includes(missingInformationFieldIdBase)) {
        newMissingInformationList = lodashUniq(
          missingInformationList.filter((fieldId) => {
            return fieldId !== missingInformationFieldIdBase;
          }),
        );
      } else {
        newMissingInformationList = lodashUniq([
          ...missingInformationList,
          missingInformationFieldIdBase,
        ]);
      }

      formikBag.setFieldValue(missingInformationListFieldId, newMissingInformationList);
      formikBag.setFieldValue(missingInformationFieldId, '');

      let updates = setIn(
        { ...formikBag.values },
        missingInformationListFieldId,
        newMissingInformationList,
      );

      updates = setIn(updates, missingInformationFieldId, null);

      onUpdateAssemblyLineApplication({
        updates,
      });
    },
    [onUpdateAssemblyLineApplication],
  );

  const onEditAssemblyLineDocumentType = ({ fromType }) => {
    setAssemblyLineApplicationUpdateDocumentTypeModalProps({
      fromType,
    });
  };

  const onCloseAssemblyLineUpdateDocumentTypeModal = () => {
    setAssemblyLineApplicationUpdateDocumentTypeModalProps(null);
  };

  const initialFormFields = getFormFields({
    values: initialValues,
    householdApplication,
  });

  if (!proofOfIncomeDocsModalOpen) {
    const stepErrors = getStepErrors({
      formFields: initialFormFields,
      values: initialValues,
      assemblyLine,
      householdApplication,
    });
    const stepErrorsByTitle = lodashGroupBy('title', stepErrors);

    const stepStatuses = getAssemblyLineVoieStatuses({
      assemblyLineVoie: initialValues,
      processingTime: assemblyLine.processingTime,
      householdApplication,
    });

    const monthlyIncomeBreakdown = calculateAssemblyLineGrossMonthlyIncome({
      ...initialValues,
      paystubsShouldCountIncomeSource: true,
      offerLetterShouldCountIncomeSource: true,
      bankStatementsShouldCountIncomeSource: true,
    });

    const showPinwheelCta =
      householdApplication.incomeVerification?.status === INCOME_VERIFICATION_STATUS_COMPLETED;

    if (
      typeof householdApplication.hasSourceOfIncome === 'boolean' &&
      !householdApplication.hasSourceOfIncome
    ) {
      return (
        <div>
          <SrText color="blueGreycliff" font="greycliff" weight="600" size="16" lineHeight="24px">
            {'The application reported that they do not have a source of income' +
              (householdApplication.payRentExplanation
                ? `, and provided this explanation: "${householdApplication.payRentExplanation}"`
                : '.')}
          </SrText>
        </div>
      );
    }

    return (
      <div>
        <SrFlex wrap="wrap" align="start">
          {(householdApplication.paystubPhotos || []).length > 0 && showPinwheelCta && (
            <SrBox width="320px" padding="0 16px 16px 0">
              <SrIncomeVerificationCardAssemblyLine
                incomeType={PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS}
                amount={monthlyIncomeBreakdown.paystubsMonthlyIncome}
                documentsCount={householdApplication.paystubPhotos.length}
                incomeShouldCount={initialValues.paystubsShouldCountIncomeSource}
                onView={() => onToggleViewDocuments(PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS)}
                assemblyLineVoie={initialValues}
                assemblyLineVoieStatuses={stepStatuses}
                assemblyLineGrossMonthlyIncome={monthlyIncomeBreakdown}
              />
            </SrBox>
          )}

          {(householdApplication.paystubPhotos || []).length > 0 && !showPinwheelCta && (
            <SrBox width="320px" padding="0 16px 16px 0">
              <SrIncomeVerificationCardAssemblyLine
                incomeType={PROOF_OF_INCOME_PAYTSTUBS}
                amount={monthlyIncomeBreakdown.paystubsMonthlyIncome}
                documentsCount={householdApplication.paystubPhotos.length}
                incomeShouldCount={initialValues.paystubsShouldCountIncomeSource}
                onView={() => onToggleViewDocuments(PROOF_OF_INCOME_PAYTSTUBS)}
                assemblyLineVoie={initialValues}
                assemblyLineVoieStatuses={stepStatuses}
                assemblyLineGrossMonthlyIncome={monthlyIncomeBreakdown}
              />
            </SrBox>
          )}

          {householdApplication.offerLetterFileUrl && (
            <SrBox width="320px" padding="0 16px 16px 0">
              <SrIncomeVerificationCardAssemblyLine
                incomeType={PROOF_OF_INCOME_OFFER_LETTER}
                amount={monthlyIncomeBreakdown.offerLetterMonthlyIncome}
                documentsCount={initialValues.offerLetterFileUrl ? 1 : 0}
                incomeShouldCount={initialValues.offerLetterShouldCountIncomeSource}
                onView={() => onToggleViewDocuments(PROOF_OF_INCOME_OFFER_LETTER)}
                assemblyLineVoie={initialValues}
                assemblyLineVoieStatuses={stepStatuses}
                assemblyLineGrossMonthlyIncome={monthlyIncomeBreakdown}
              />
            </SrBox>
          )}

          {(householdApplication.bankStatementFileUrls || []).length > 0 && (
            <SrBox width="320px" padding="0 16px 16px 0">
              <SrIncomeVerificationCardAssemblyLine
                incomeType={PROOF_OF_INCOME_BANK_STATEMENTS}
                amount={monthlyIncomeBreakdown.bankStatementsMonthlyIncome}
                documentsCount={householdApplication.bankStatementFileUrls.length}
                incomeShouldCount={initialValues.bankStatementsShouldCountIncomeSource}
                onView={() => onToggleViewDocuments(PROOF_OF_INCOME_BANK_STATEMENTS)}
                assemblyLineVoie={initialValues}
                assemblyLineVoieStatuses={stepStatuses}
                assemblyLineGrossMonthlyIncome={monthlyIncomeBreakdown}
              />
            </SrBox>
          )}

          {(householdApplication.additionalProofOfIncomePhotos || []).length > 0 && (
            <SrBox width="320px" padding="0 16px 16px 0">
              <SrIncomeVerificationCardAssemblyLine
                incomeType={PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS}
                amount={monthlyIncomeBreakdown.additionalProofOfMonthlyIncomeTotal}
                documentsCount={householdApplication.additionalProofOfIncomePhotos.length}
                incomeShouldCount={
                  (initialValues.additionalProofOfIncomePhotos || []).every(
                    ({ shouldCountIncomeSource }) => typeof shouldCountIncomeSource !== 'boolean',
                  )
                    ? null
                    : (initialValues.additionalProofOfIncomePhotos || []).some(
                        ({ shouldCountIncomeSource }) => shouldCountIncomeSource,
                      )
                }
                onView={() => onToggleViewDocuments(PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS)}
                assemblyLineVoie={initialValues}
                assemblyLineVoieStatuses={stepStatuses}
                assemblyLineGrossMonthlyIncome={monthlyIncomeBreakdown}
              />
            </SrBox>
          )}
        </SrFlex>
        {stepErrors.length > 0 && (
          <SrBox padding="20px 0">
            <SrText font="greycliff" size="18" color="orange300" weight="700">
              Information Required
            </SrText>
            {Object.keys(stepErrorsByTitle).map((title) => (
              <SrBox padding="10px 0" key={title}>
                <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_ORANGE}>
                  {title}:
                  <br />
                  {stepErrorsByTitle[title].map((stepError) => (
                    <div key={`${stepError.title}-${stepError.fieldId}-${stepError.message}`}>
                      {stepError.message}
                      <br />
                    </div>
                  ))}
                </SrErrorMessage>
              </SrBox>
            ))}
            <SrBox paddingTop="10px">
              <SrText color="orange300" font="greycliff" weight="600" size="16" lineHeight="24px">
                Once you've asked the applicant for these changes move on to the next applicant in
                this household.
              </SrText>
            </SrBox>
          </SrBox>
        )}
        <SrIncomeVerificationTotalCardAssemblyLine
          amount={calculateAssemblyLineGrossMonthlyIncome(initialValues).grossMonthlyIncome}
          status={stepStatuses.status}
        />
      </div>
    );
  }

  let modalPhotoUrls = proofOfIncomeDocsModalOpen
    ? householdApplication[
        PROOF_OF_INCOME_TO_HOUSEHOLD_APPPLICATION_FIELD_MAPPING[proofOfIncomeDocsModalOpen]
      ]
    : null;

  if (modalPhotoUrls && !Array.isArray(modalPhotoUrls)) {
    modalPhotoUrls = [modalPhotoUrls];
  }

  return (
    <>
      {proofOfIncomeDocsModalOpen && (
        <SrModalSplitView
          onClose={() => setProofOfIncomeDocsModalOpen(null)}
          leftChild={(modalPhotoUrls || []).map((photoUrl) => (
            <SrBox key={photoUrl} textAlign="center">
              <SrS3FileDisplay url={photoUrl} maxWidth="100%" maxHeight="800px" withRotateButton />
            </SrBox>
          ))}
          rightChild={
            <Formik
              initialValues={createInitialValues({
                formFields: initialFormFields,
                defaultValues: initialValues,
              })}
              validate={(values) => {
                const formFields = getFormFields({
                  values,
                  proofOfIncomeType: proofOfIncomeDocsModalOpen,
                  householdApplication,
                });
                return createFormValidationFn(formFields)(values);
              }}
              onSubmit={emptyFunction}
              render={(formikBag) => {
                const stepStatuses = getAssemblyLineVoieStatuses({
                  assemblyLineVoie: formikBag.values,
                  processingTime: assemblyLine.processingTime,
                  householdApplication,
                });

                const onChange = (formFieldId, value) => {
                  formikBag.setFieldValue(formFieldId, value);
                  onUpdateAssemblyLineApplication({
                    updates: setIn(formikBag.values, formFieldId, value),
                  });
                };

                const formFields = getFormFields({
                  values: formikBag.values,
                  onChange,
                  proofOfIncomeType: proofOfIncomeDocsModalOpen,
                  householdApplication,
                });

                const stepErrors = getStepErrors({
                  formFields,
                  assemblyLine,
                  householdApplication,
                  values: formikBag.values,
                });

                const grossMonthlyIncome = calculateAssemblyLineGrossMonthlyIncome(formikBag.values)
                  .grossMonthlyIncome;

                // eslint-disable-next-line max-len
                const paystubsGrossMonthlyIncomeRaw = calculatePaystubsGrossMonthlyIncomeRaw(
                  formikBag.values.paystubs,
                );

                return (
                  <Form>
                    <SrApplicationAssemblyLineContainer
                      title={`Income Verification (${PROOF_OF_INCOME_TYPE_TO_LABEL_MAPPING[proofOfIncomeDocsModalOpen]})`}
                      isExpanded
                      status={status}
                      borderType={SR_COLLAPSIBLE_CONTAINER_BORDER_TYPE_BOTTOM_GRAY}
                      onEditClick={
                        proofOfIncomeDocsModalOpen !== PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS
                          ? () =>
                              onEditAssemblyLineDocumentType({
                                fromType: proofOfIncomeDocsModalOpen,
                              })
                          : null
                      }
                      editButtonTheme={SR_COLLAPSIBLE_CONTAINER_EDIT_BUTTON_THEME_BLUE}
                    >
                      {proofOfIncomeDocsModalOpen === PROOF_OF_INCOME_OFFER_LETTER && (
                        <SrAssemblyLineFormFields
                          formFields={formFields.filter(({ id }) => id.includes('offerLetter'))}
                          formikBag={formikBag}
                          stepErrors={stepErrors}
                          missingInformationListFieldId="offerLetterMissingInformationFieldIds"
                          onMissingInformationClick={onMissingInformationClick}
                        />
                      )}
                      {(proofOfIncomeDocsModalOpen === PROOF_OF_INCOME_PAYTSTUBS ||
                        proofOfIncomeDocsModalOpen === PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS) && (
                        <div>
                          {lodashTimes((householdApplication.paystubPhotos || []).length, (index) =>
                            formFields.filter(({ id }) => id.includes(`paystubs.${index}`)),
                          ).map((formFields, index) => (
                            <SrBox
                              marginTop="24px"
                              marginBottom="24px"
                              key={index} /* eslint-disable-line react/no-array-index-key */
                            >
                              <SrApplicationAssemblyLineContainer
                                title={`Paystub ${index + 1}`}
                                isExpanded={paystubExpandedState[index]}
                                onClick={() => onClickPaystubRow(index)}
                                status={(stepStatuses.paystubs || [])[index]}
                                borderType={SR_COLLAPSIBLE_CONTAINER_BORDER_TYPE_ROUNDED}
                              >
                                <SrAssemblyLineFormFields
                                  formFields={formFields}
                                  formikBag={formikBag}
                                  stepErrors={stepErrors}
                                  // eslint-disable-next-line max-len
                                  missingInformationListFieldId={`paystubs.${index}.missingInformationFieldIds`}
                                  onMissingInformationClick={onMissingInformationClick}
                                  onContinueClick={() => onClickPaystubContinueButton(index)}
                                  payrollTypes={paystubsGrossMonthlyIncomeRaw[index]}
                                />
                              </SrApplicationAssemblyLineContainer>
                            </SrBox>
                          ))}
                          <SrAssemblyLineFormFields
                            formFields={formFields.filter(({ id }) =>
                              id.includes('paystubsShould'),
                            )}
                            formikBag={formikBag}
                            stepErrors={stepErrors}
                          />
                        </div>
                      )}
                      {proofOfIncomeDocsModalOpen === PROOF_OF_INCOME_BANK_STATEMENTS && (
                        <div>
                          {lodashTimes(
                            (householdApplication.bankStatementFileUrls || []).length,
                            (index) =>
                              formFields.filter(({ id }) => id.includes(`bankStatements.${index}`)),
                          ).map((formFields, index) => (
                            <SrBox
                              marginTop="24px"
                              marginBottom="16px"
                              key={index} /* eslint-disable-line react/no-array-index-key */
                            >
                              <SrApplicationAssemblyLineContainer
                                title={`Bank Statement ${index + 1}`}
                                isExpanded={bankStatementExpandedState[index]}
                                onClick={() => onClickBankStatementRow(index)}
                                status={(stepStatuses.bankStatements || [])[index]}
                                borderType={SR_COLLAPSIBLE_CONTAINER_BORDER_TYPE_ROUNDED}
                              >
                                <SrAssemblyLineFormFields
                                  formFields={formFields}
                                  formikBag={formikBag}
                                  stepErrors={stepErrors}
                                  missingInformationListFieldId={`bankStatements.${index}.missingInformationFieldIds`}
                                  onMissingInformationClick={onMissingInformationClick}
                                  onContinueClick={() => onClickBankStatementContinueButton(index)}
                                />
                              </SrApplicationAssemblyLineContainer>
                            </SrBox>
                          ))}
                          <SrAssemblyLineFormFields
                            formFields={formFields.filter(({ id }) =>
                              id.includes('bankStatementsShould'),
                            )}
                            formikBag={formikBag}
                            stepErrors={stepErrors}
                          />
                        </div>
                      )}
                      {proofOfIncomeDocsModalOpen === PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS &&
                        lodashTimes(
                          (householdApplication.additionalProofOfIncomePhotos || []).length,
                          (index) =>
                            formFields.filter(({ id }) =>
                              id.includes(`additionalProofOfIncomePhotos.${index}`),
                            ),
                        ).map((formFields, index) => (
                          <SrBox
                            marginTop="24px"
                            marginBottom="16px"
                            key={index} /* eslint-disable-line react/no-array-index-key */
                          >
                            <SrApplicationAssemblyLineContainer
                              title={`Document ${index + 1}`}
                              isExpanded={additionalProofOfIncomeExpandedState[index]}
                              onClick={() => onClickAdditionalProofOfIncomeRow(index)}
                              status={(stepStatuses.additionalProofOfIncomePhotos || [])[index]}
                              borderType={SR_COLLAPSIBLE_CONTAINER_BORDER_TYPE_ROUNDED}
                            >
                              <SrAssemblyLineFormFields
                                formFields={formFields}
                                formikBag={formikBag}
                                stepErrors={stepErrors}
                                missingInformationListFieldId={`additionalProofOfIncomePhotos.${index}.missingInformationFieldIds`}
                                onMissingInformationClick={onMissingInformationClick}
                                onContinueClick={() =>
                                  onClickAdditionalProofOfIncomeContinueButton(index)
                                }
                              />
                            </SrApplicationAssemblyLineContainer>
                          </SrBox>
                        ))}

                      <SrBox display="inlineBlock">
                        <SrFlex
                          border="1px solid #EAEBEC"
                          borderRadius="8px"
                          padding="16px"
                          marginTop="24px"
                        >
                          <SrInfoTile
                            theme={SR_INFO_TILE_THEME_ADMIN_GREY}
                            label="Applicant’s stated monthly income"
                            value={formatPrice(statedMonthlyIncome.monthlyIncome)}
                          />
                          <SrBox
                            height="35px"
                            width="1px"
                            backgroundColor="#EAEBEC"
                            margin="0 24px"
                          />
                          <SrInfoTile
                            theme={SR_INFO_TILE_THEME_ADMIN_GREY}
                            label="Calculated gross monthly income"
                            value={grossMonthlyIncome ? formatPrice(grossMonthlyIncome) : '--'}
                            valueColor={
                              SR_APPLICATION_ASSEMBLY_LINE_STATUS_TO_COLOR_MAPPING[
                                SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED
                              ]
                            }
                          />
                        </SrFlex>
                      </SrBox>
                      <SrBox paddingTop="24px">
                        <SrButton
                          theme={SR_BUTTON_THEME_ASSEMBLY_LINE_BLUE}
                          hasBoxShadow
                          onClick={() => onClickCompleteButton({ formikBag })}
                        >
                          Complete
                        </SrButton>
                      </SrBox>
                    </SrApplicationAssemblyLineContainer>
                  </Form>
                );
              }}
            />
          }
        />
      )}
      {assemblyLineApplicationUpdateDocumentTypeModalProps && (
        <SrApplicationAssemblyLineUpdateDocumentsTypeModal
          assemblyLineApplicationId={householdApplication.assemblyLineApplication.id}
          fromType={assemblyLineApplicationUpdateDocumentTypeModalProps.fromType}
          onClose={onCloseAssemblyLineUpdateDocumentTypeModal}
        />
      )}
    </>
  );
};

SrApplicationAssemblyLineStepIncomeVerification.propTypes = {
  householdApplication: PropTypes.object.isRequired,
  updateAssemblyLineApplication: PropTypes.func.isRequired,
  assemblyLine: PropTypes.object,
};

SrApplicationAssemblyLineStepIncomeVerification.stepTitle = 'Income Verification';
SrApplicationAssemblyLineStepIncomeVerification.displayName =
  'SrApplicationAssemblyLineStepIncomeVerification'; // eslint-disable-line max-len

SrApplicationAssemblyLineStepIncomeVerification.getStatus = ({
  householdApplication,
  assemblyLine,
}) => {
  const values = getInitialAssemblyLineVOIEFormState(householdApplication);
  const stepStatuses = getAssemblyLineVoieStatuses({
    assemblyLineVoie: values,
    processingTime: assemblyLine.processingTime,
    householdApplication,
  });

  return stepStatuses.status;
};

export default SrApplicationAssemblyLineStepIncomeVerification;
